import { useState } from "react";
import { useTranslation } from "react-i18next";
// TODO: still some Dropdown left to clean up here
import { ButtonGroup, Divider, Dropdown, DropdownItem, DropdownMenu, Form, Grid, Icon, Modal } from "semantic-ui-react";

import { DATE_FORMATS_FNS, PinTypeIcon, useCan, useGetPinOptionsDropdownOptions } from "components";
import { InterventionSubitems } from "components/InterventionModal/components";
import "components/InterventionModal/InterventionModal.scss";
import ENV from "config/Env";
import { usePermanentRealTimeUser, useUploadAttachment } from "hooks";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, PIN_TYPES } from "models";
import { canAddSubitems } from "modules/AppointmentDetails/components/Interventions/utils";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { InterventionFormAttachment, InterventionModalFormValues } from "components";
import type { Dealer, DealerLocation, InterventionElement } from "models";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";

type InterventionModalFormProps = {
  isLoading: boolean;
  hasErrors: boolean;
  interventionSubitems: InterventionElement[];
  dealer?: Dealer | null;
  location?: DealerLocation | null;
  intervention?: AddInterventionPayload | null;
  appointmentId?: number;
  showFileUpload?: boolean;
  showAddSubitems?: boolean;
  isCarRecallPinType?: boolean;
  canWriteBackCustomerData?: boolean;
  onClose: () => void;
  onSubmit: (values: InterventionModalFormValues) => void;
  handleUpdateInterventionElements: (value: InterventionElement[]) => void;
};

const FormHelper = createFormHelper<InterventionModalFormValues, InterventionFormAttachment>();

export const InterventionModalForm = ({
  location,
  dealer,
  isLoading,
  hasErrors,
  intervention,
  appointmentId,
  showFileUpload,
  showAddSubitems,
  isCarRecallPinType,
  interventionSubitems,
  canWriteBackCustomerData,
  onClose,
  onSubmit,
  handleUpdateInterventionElements
}: InterventionModalFormProps) => {
  const { t } = useTranslation();
  const { handleSubmit, setValue, watch } = FormHelper.useFormContext();
  const [pinType, description, customerOk, title] = watch(["pin_type_id", "description", "customer_ok", "title"]);

  const [showSubitemsModal, setShowSubitemsModal] = useState(false);
  const [showPinTypeDropdown, setShowPinTypeDropdown] = useState(false);

  const canUpdateIntervention = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Interventions);
  const canCreateFullPin = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.PinFull);
  const { user } = usePermanentRealTimeUser();
  const { pinTypeOptions } = useGetPinOptionsDropdownOptions(dealer);
  const { handleUploadFileAttachment, isUploadingAttachment } = useUploadAttachment("/interventions/attachment/upload", ENV.appointmentBaseURL);

  const handleToggleCustomerOk = () => setValue("customer_ok", !customerOk);
  const togglePinTypeDropdown = () => !isCarRecallPinType && setShowPinTypeDropdown(prev => !prev);

  const handleOpenSubitemsModal = () => setShowSubitemsModal(true);
  const handleCloseSubitemsModal = () => setShowSubitemsModal(false);

  const handleReplaceTitleWithDescription = () => {
    setValue("title", description);
    setValue("description", "");
  };

  const getPinTypeIcon = (pinTypeId: PIN_TYPES | null) =>
    pinTypeId ? pinTypeOptions.find(option => option.value === pinTypeId)?.icon : <PinTypeIcon pinTypeId={isCarRecallPinType ? PIN_TYPES.Recall : 0} />;

  const handleUploadAttachment = async (file: File): Promise<InterventionFormAttachment | null> => {
    const extension = file.name.lastIndexOf(".") > -1 ? file.name.slice(file.name.lastIndexOf(".") + 1) : "unknown";

    const formData = new FormData();
    formData.append("appointment_id", String(appointmentId || intervention?.appointment_id));
    formData.append("file", file);

    const url = await handleUploadFileAttachment({ formData });
    if (!url) return null;

    return {
      url,
      type: file.type || extension,
      name: file.name,
      created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
      updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
      user_id: user?.id ?? 0,
      appointment_id: intervention?.appointment_id || appointmentId,
      visible_to_customer: true
    };
  };

  return (
    <>
      <Modal.Header className="header-container">
        <div className="header-title-container">
          <div className="header-icon">
            <Icon className="screwdriver wrench" />
          </div>
          <p className="header-title">{intervention ? t("v8_convert_to_intervention", "Convert to Intervention") : t("v8_add_intervention", "Add Intervention")}</p>
        </div>
      </Modal.Header>
      <Divider className="mt-top-0" />

      <Modal.Content className="content-container">
        <Grid>
          <Grid.Column width={10}>
            <Form.Field required error={hasErrors && !title}>
              <label>{t("v8_title", "Title")}</label>
              <FormHelper.Field.Input name="title" inputProps={{ placeholder: t("v8_title", "Title") }} />
            </Form.Field>
          </Grid.Column>

          <Grid.Column width={6}>
            <div className="price-label-customer-ok-container">
              {location?.dms_price_enabled && (
                <Grid.Column width={4} className="price-container">
                  <Form.Field>
                    <label>{t("v8_fixed_price", "Fixed Price")}</label>
                    <FormHelper.Field.Input
                      name="price"
                      isPriceInput={true}
                      inputProps={{
                        disabled: canWriteBackCustomerData,
                        fluid: true,
                        labelPosition: "right",
                        label: { basic: true, content: <Icon name="euro" /> }
                      }}
                    />
                  </Form.Field>
                </Grid.Column>
              )}

              {canCreateFullPin && (
                <Form.Field>
                  <label>{t("v8_label", "Label")}</label>
                  <ButtonGroup className="with-dropdown">
                    <Form.Button type="button" className={`${pinType ? "black" : "grey"}`} onClick={togglePinTypeDropdown}>
                      {getPinTypeIcon(pinType)}
                    </Form.Button>
                    <Dropdown className="button icon" floating open={showPinTypeDropdown} onOpen={togglePinTypeDropdown} onClose={togglePinTypeDropdown}>
                      <DropdownMenu>
                        <DropdownItem onClick={() => setValue("pin_type_id", null)} className="no-label">
                          <Icon name="pin" />
                          {t("v8_no_label", "No Label")}
                        </DropdownItem>
                        {pinTypeOptions.map(({ key, text, value, icon }) => (
                          <DropdownItem key={key} icon={icon} text={text} value={value} onClick={() => setValue("pin_type_id", value)} />
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </ButtonGroup>
                </Form.Field>
              )}

              <Form.Field className={`customer-ok-container ${!canCreateFullPin ? "mt-18" : ""}`}>
                <Form.Button onClick={handleToggleCustomerOk} type="button" className="customer-ok-button" icon basic>
                  <Icon className={customerOk ? "check circle green" : "xmark circle red"} />
                </Form.Button>
              </Form.Field>
            </div>
          </Grid.Column>
        </Grid>

        <Grid>
          <Form.Field width={16}>
            <div className="desc-container">
              <label className="desc-label">{t("v8_remarks", "Remarks")}</label>
              <Form.Button basic onClick={handleReplaceTitleWithDescription} icon className="desc-icon-button" disabled={!description}>
                <Icon className="arrow up from line solid desc-icon" />
              </Form.Button>
            </div>
            <FormHelper.Field.TextArea name="description" textAreaProps={{ rows: 2, placeholder: t("v8_enter_remarks_here", "Enter remarks here") }} />
          </Form.Field>
        </Grid>

        {appointmentId && showFileUpload && (
          <Grid>
            <Form.Field width={16}>
              <FormHelper.Field.Attachments
                name="attachments"
                isUploading={isUploadingAttachment}
                onUpload={handleUploadAttachment}
                onPreviewAttachments={attachments => attachments.map(({ url, name, created_on, user_id }) => ({ url, name, created_on, user_id }))}
              />
            </Form.Field>
          </Grid>
        )}

        <Grid>
          <Grid.Column>
            {canAddSubitems(location, true) && showAddSubitems && (
              <Form.Button icon color="green" type="button" onClick={handleOpenSubitemsModal} className="mt-14">
                <Icon className="plus" />
                {t("v8_subitems", "Subitems")}
              </Form.Button>
            )}
            <InterventionSubitems
              location={location}
              interventionSubItems={interventionSubitems}
              showInterventionSubItemModal={showSubitemsModal}
              formSetPrice={(price: number) => setValue("price", price)}
              handleOpenSubitemsModal={handleOpenSubitemsModal}
              handleCloseSubitemsModal={handleCloseSubitemsModal}
              handleUpdateInterventionElements={handleUpdateInterventionElements}
            />
          </Grid.Column>
        </Grid>
      </Modal.Content>
      <Divider className="mb-0" />

      <Modal.Actions className="action-buttons-container">
        <div className="save-cancel-buttons-container">
          <Form.Button className="light" onClick={onClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Form.Button>
          <Form.Button color="green" onClick={handleSubmit(d => onSubmit(d))} loading={isLoading} disabled={isLoading || isUploadingAttachment || !canUpdateIntervention}>
            {t("v8_save", "Save")}
            <Icon className="check" />
          </Form.Button>
        </div>
      </Modal.Actions>
    </>
  );
};
